<template>
  <div>
    <Head title="Items" />
    <h1 class="mb-8 text-3xl font-bold">
      Master
      <span class="text-indigo-400 font-medium"> / </span>
      Items
    </h1>
    <div class="flex items-center justify-between mb-6">
      <search-filter
        v-model="form.search"
        class="mr-4 w-full max-w-md"
        @reset="reset">
        <label class="block text-gray-700">Type:</label>
        <select v-model="form.types" class="form-select mt-1 w-full">
          <option :value="null">
            [ALL]
          </option>
          <option value="STOCK">
            STOCK
          </option>
          <option value="SERVICE">
            SERVICE
          </option>
        </select>
        <label class="block text-gray-700 mt-4">Main Category:</label>
        <select v-model="form.main" class="form-select mt-1 w-full">
          <option :value="null">
            [ALL]
          </option>
          <option
            v-for="main in itemInvMains"
            :key="main.id"
            :value="main.id">
            {{ main.name }}
          </option>
        </select>
        <label class="block text-gray-700 mt-4">Sub Category:</label>
        <select
          v-model="form.sub"
          class="form-select mt-1 w-full"
          :disabled="!form.main">
          <option :value="null">
            [ALL]
          </option>
          <option
            v-for="sub in availableSubs"
            :key="sub.id"
            :value="sub.id">
            {{ sub.name }}
          </option>
        </select>
      </search-filter>
      <Link class="btn-indigo" href="/items/create">
        <span>Create</span>
        <span class="hidden md:inline">&nbsp;Item</span>
      </Link>
    </div>
    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <thead>
          <tr class="text-left font-bold">
            <th class="pb-4 pt-6 px-6">
              Code
            </th>
            <th class="pb-4 pt-6 px-6">
              Name
            </th>
            <th class="pb-4 pt-6 px-6">
              Default Unit
            </th>
            <th class="pb-4 pt-6 px-6">
              Large Unit
            </th>
            <th class="pb-4 pt-6 px-6">
              Convert
            </th>
            <th class="pb-4 pt-6 px-6">
              Type
            </th>
            <th class="pb-4 pt-6 px-4">
              Inv. Type
            </th>
            <th class="pb-4 pt-6 px-4" colspan="2">
              Last Updated
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in items.data"
            :key="item.id"
            class="hover:bg-gray-100 focus-within:bg-gray-100">
            <td class="border-t">
              <Link
                class="flex items-center px-6 py-4"
                :href="`/items/${item.id}/edit`"
                tabindex="-1">
                {{ item.code }}
              </Link>
            </td>
            <td class="border-t">
              <Link
                class="flex items-center px-6 py-4"
                :href="`/items/${item.id}/edit`"
                tabindex="-1">
                {{ item.name }}
              </Link>
            </td>
            <td class="border-t">
              <Link
                class="flex items-center px-6 py-4"
                :href="`/items/${item.id}/edit`"
                tabindex="-1">
                {{ item.unit_small.name }}
              </Link>
            </td>
            <td class="border-t">
              <Link
                class="flex items-center px-6 py-4"
                :href="`/items/${item.id}/edit`"
                tabindex="-1">
                {{ item.unit_large.name }}
              </Link>
            </td>
            <td class="border-t text-right">
              <Link
                class="px-6 py-4"
                :href="`/items/${item.id}/edit`"
                tabindex="-1">
                {{ item.convert_value.toLocaleString('id-ID') }}
              </Link>
            </td>
            <td class="border-t">
              <Link
                class="flex items-center px-6 py-4"
                :href="`/items/${item.id}/edit`"
                tabindex="-1">
                {{ item.type }}
              </Link>
            </td>
            <td class="border-t">
              <Link
                class="flex items-center px-6 py-4"
                :href="`/items/${item.id}/edit`"
                tabindex="-1">
                {{ item.item_inv_type }}
              </Link>
            </td>
            <td class="border-t">
              <Link
                class="flex items-center px-6 py-4"
                :href="`/items/${item.id}/edit`"
                tabindex="-1">
                {{ item.updated_at }}
              </Link>
            </td>
            <td class="w-px border-t">
              <Link
                class="flex items-center px-4"
                :href="`/items/${item.id}/edit`"
                tabindex="-1">
                <icon name="cheveron-right" class="block w-6 h-6 fill-gray-400" />
              </Link>
            </td>
          </tr>
          <tr v-if="items.data.length === 0">
            <td class="px-6 py-4 border-t" colspan="6">
              No items found.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <pagination class="mt-6" :links="items.links" />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination'
import SearchFilter from '@/Shared/SearchFilter'

export default {
    components: {
        Head,
        Icon,
        Link,
        Pagination,
        SearchFilter,
    },
    layout: Layout,
    props: {
        filters: Object,
        items: Object,
        itemInvMains: Array,
        itemInvSubs: Array,
    },
    data () {
        return {
            form: {
                search: this.filters.search,
                types: this.filters.types,
                main: this.filters.main,
                sub: this.filters.sub,
            },
        }
    },
    computed: {
        availableSubs () {
            if (!this.form.main) return []
            return this.itemInvSubs.filter(s => s.parent_id === this.form.main)
        },
    },
    watch: {
        'form.main' () {
            if (this.form.sub && !this.availableSubs.some(s => s.id === this.form.sub)) {
                this.form.sub = null
            }
        },
        form: {
            deep: true,
            handler: throttle(function () {
                this.$inertia.get('/items', pickBy(this.form), { preserveState: true })
            }, 150),
        },
    },
    methods: {
        reset () {
            this.form = mapValues(this.form, () => null)
        },
    },
}
</script>
